<template>
    <div class="pnr-refus">
        <div class="pnr-refus-card">
            <div class="pnr-refus-badge" :class="'is-' + msg.ton">
                <i :class="msg.icone"></i>
            </div>

            <h1 class="pnr-refus-title">{{ msg.titre }}</h1>
            <p class="pnr-refus-text">{{ msg.texte }}</p>

            <div class="pnr-refus-actions">
                <button v-if="msg.retryable && retryUrl" class="pnr-refus-primary" @click="reessayer">
                    <i class="ri-refresh-line"></i> Réessayer
                </button>
                <div v-else class="pnr-refus-hint">
                    <i class="ri-qr-scan-2-line"></i>
                    <span>{{ indication }}</span>
                </div>
            </div>

            <div class="pnr-refus-code" v-if="c">Code : {{ c }}</div>
        </div>
    </div>
</template>

<script>
    /* eslint-disable */

    // Les textes sont volontairement figés côté client : le message renvoyé par
    // le backend n'est jamais affiché tel quel (il est fait pour les logs, pas
    // pour le client, et ne doit pas pouvoir injecter du contenu dans la page).
    const MESSAGES = {
        KEY_INVALID: {
            icone: "ri-qr-code-line", ton: "stop", retryable: false,
            titre: "Ce QR code n'est plus valide",
            texte: "Le code que vous avez scanné a été remplacé. Demandez le QR code à jour à l'accueil du magasin.",
        },
        LICENCE_EXPIRED: {
            icone: "ri-shield-cross-line", ton: "stop", retryable: false,
            titre: "Service indisponible",
            texte: "Le service Scan & Go n'est pas disponible dans ce magasin. Vous pouvez régler vos achats directement en caisse.",
        },
        DEVICE_QUOTA_EXCEEDED: {
            icone: "ri-smartphone-line", ton: "wait", retryable: true,
            titre: "Trop d'appareils connectés",
            texte: "Le nombre maximum d'appareils utilisant le service est atteint. Réessayez dans quelques minutes, ou voyez un agent du magasin.",
        },
        MAG_UNKNOWN: {
            icone: "ri-store-2-line", ton: "stop", retryable: false,
            titre: "Magasin introuvable",
            texte: "Ce lien ne correspond à aucun magasin. Vérifiez que vous avez bien scanné le QR code affiché sur place.",
        },
        MAG_CLOSED: {
            icone: "ri-time-line", ton: "wait", retryable: true,
            titre: "Magasin fermé",
            texte: "Ce magasin est actuellement fermé. Le service Scan & Go sera de nouveau disponible à la réouverture.",
        },
        PARAMS_INVALID: {
            icone: "ri-link-unlink", ton: "stop", retryable: false,
            titre: "Lien invalide",
            texte: "Ce lien est incomplet. Rescannez le QR code du magasin pour ouvrir votre panier.",
        },
        OFFLINE_NO_CACHE: {
            icone: "ri-wifi-off-line", ton: "wait", retryable: true,
            titre: "Pas de connexion",
            texte: "Impossible de charger le catalogue du magasin. Vérifiez votre connexion, puis réessayez.",
        },
    };

    const FALLBACK = {
        icone: "ri-error-warning-line", ton: "stop", retryable: true,
        titre: "Accès impossible",
        texte: "Nous ne pouvons pas ouvrir votre panier pour le moment. Rescannez le QR code du magasin ou voyez un agent.",
    };

    // Le même écran sert les deux parcours. Seuls les textes qui parlent de
    // « panier », de « magasin » ou de « caisse » changent : un client attablé
    // au restaurant ne va pas régler ses achats en caisse. Les codes, les tons
    // et les icônes sont identiques - c'est le même backend qui les émet.
    //
    // Ne redéfinir ici QUE les entrées dont la formulation diffère : toute
    // valeur absente retombe sur MESSAGES, ce qui évite d'avoir à maintenir
    // deux tables complètes en parallèle.
    const TEXTES_MENU = {
        KEY_INVALID: "Le code que vous avez scanné a été remplacé. Demandez le QR code à jour à un serveur.",
        LICENCE_EXPIRED: "La commande depuis votre téléphone n'est pas disponible dans ce restaurant. Adressez-vous à un serveur.",
        MAG_UNKNOWN: "Ce lien ne correspond à aucun restaurant. Vérifiez que vous avez bien scanné le QR code affiché sur place.",
        MAG_CLOSED: "Ce restaurant est actuellement fermé. La carte sera de nouveau accessible à la réouverture.",
        PARAMS_INVALID: "Ce lien est incomplet. Rescannez le QR code de votre table pour afficher la carte.",
        OFFLINE_NO_CACHE: "Impossible de charger la carte du restaurant. Vérifiez votre connexion, puis réessayez.",
        DEVICE_QUOTA_EXCEEDED: "Le nombre maximum d'appareils utilisant le service est atteint. Réessayez dans quelques minutes, ou appelez un serveur.",
        __fallback: "Nous ne pouvons pas afficher la carte pour le moment. Rescannez le QR code de votre table ou appelez un serveur.",
        __hint: "Rescannez le QR code affiché sur votre table.",
    };

    // Le personnel n'est pas un client : on ne lui dit pas de « voir un agent »
    // ni de « régler en caisse », mais de s'adresser à son manager.
    const TEXTES_SERVEUR = {
        KEY_INVALID: "Le QR d'accès du restaurant a été remplacé. Demandez l'affiche à jour à votre manager.",
        LICENCE_EXPIRED: "La prise de commande mobile n'est pas activée pour cet établissement. Voyez avec votre manager.",
        MAG_UNKNOWN: "Ce lien ne correspond à aucun établissement. Vérifiez l'affiche que vous avez scannée.",
        MAG_CLOSED: "Cet établissement est actuellement fermé. Le service reprendra à la réouverture.",
        PARAMS_INVALID: "Ce lien est incomplet. Rescannez l'affiche du restaurant pour ouvrir votre service.",
        OFFLINE_NO_CACHE: "Impossible de charger la carte. Vérifiez votre connexion, puis réessayez.",
        DEVICE_QUOTA_EXCEEDED: "Le nombre maximum d'appareils est atteint. Voyez avec votre manager pour libérer une place.",
        __fallback: "Impossible d'ouvrir votre service pour le moment. Rescannez l'affiche ou voyez votre manager.",
        __hint: "Rescannez l'affiche du restaurant.",
    };

    export default {
        name: "panierrefus",
        props: {
            c: { type: String, default: "" },
            // 'menu' pour le parcours restaurant, vide pour le Scan & Go.
            t: { type: String, default: "" },
        },
        data: () => ({
            retryUrl: "",
        }),
        computed: {
            // Table de formulations du parcours courant, ou null pour le
            // Scan & Go, dont les textes d'origine font foi.
            variante() {
                if (this.t === "menu") return TEXTES_MENU;
                if (this.t === "serveur") return TEXTES_SERVEUR;
                return null;
            },
            msg() {
                const base = MESSAGES[this.c] || FALLBACK;
                if (!this.variante) return base;
                const remplace = MESSAGES[this.c]
                    ? this.variante[this.c]
                    : this.variante.__fallback;
                return remplace ? { ...base, texte: remplace } : base;
            },
            indication() {
                return this.variante
                    ? this.variante.__hint
                    : "Rescannez le QR code affiché en magasin.";
            },
        },
        methods: {
            reessayer() {
                if (!this.retryUrl) return;
                this.$router.replace(this.retryUrl);
            },
        },
        mounted() {
            // La clé de licence ne doit jamais transiter par une URL visible :
            // /panier la dépose ici avant de rediriger. sessionStorage est propre
            // à l'onglet et disparaît à sa fermeture.
            try {
                this.retryUrl = sessionStorage.getItem("sg_retry_url") || "";
            } catch (e) {
                this.retryUrl = "";
            }
        },
    };
</script>

<style scoped>
    .pnr-refus {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background: #F4F6F5;
        font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        overflow-y: auto;
    }

    .pnr-refus-card {
        width: 100%;
        max-width: 420px;
        background: #fff;
        border-radius: 20px;
        padding: 36px 26px 26px;
        text-align: center;
        box-shadow: 0 10px 34px rgba(11, 61, 46, .10);
    }

    .pnr-refus-badge {
        width: 78px;
        height: 78px;
        margin: 0 auto 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
    }

    .pnr-refus-badge.is-stop { background: #FDECEC; color: #C0392B; }
    .pnr-refus-badge.is-wait { background: #FEF5E7; color: #B9770E; }

    .pnr-refus-title {
        margin: 0 0 10px;
        font-size: 21px;
        font-weight: 700;
        color: #0B3D2E;
    }

    .pnr-refus-text {
        margin: 0 0 26px;
        font-size: 15px;
        line-height: 1.55;
        color: #5A6B65;
    }

    .pnr-refus-actions { margin-bottom: 18px; }

    .pnr-refus-primary {
        width: 100%;
        padding: 15px;
        border: 0;
        border-radius: 12px;
        background: #0F6E56;
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    .pnr-refus-primary:active { background: #0B5442; }

    .pnr-refus-hint {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px;
        border-radius: 12px;
        background: #F4F6F5;
        color: #5A6B65;
        font-size: 14px;
    }

    .pnr-refus-code {
        font-size: 11px;
        letter-spacing: .5px;
        color: #A8B4B0;
    }
</style>
